import {
  createORMClient,
  LiveData,
  ObjectPool,
  Service,
  YjsDBAdapter,
} from '@toeverything/infra';
import { Doc as YDoc } from 'yjs';

import type { WorkspaceServerService } from '../../cloud';
import { AuthService } from '../../cloud/services/auth';
import type { WorkspaceService } from '../../workspace';
import { WorkspaceDB, type WorkspaceDBWithTables } from '../entities/db';
import {
  AFFiNE_WORKSPACE_DB_SCHEMA,
  AFFiNE_WORKSPACE_USERDATA_DB_SCHEMA,
  type AFFiNEWorkspaceDbSchema,
  type AFFiNEWorkspaceUserdataDbSchema,
} from '../schema';

const WorkspaceDBClient = createORMClient(AFFiNE_WORKSPACE_DB_SCHEMA);
const WorkspaceUserdataDBClient = createORMClient(
  AFFiNE_WORKSPACE_USERDATA_DB_SCHEMA
);

/**
 * sl-oiya.5: Tables that require Owner/Admin role for Y.js sync.
 * These contain sensitive metadata (folder names, doc assignments) that
 * must not be synced to non-privileged users via CRDT.
 * Members get this data via server-filtered GraphQL queries instead.
 */
const RESTRICTED_SYNC_TABLES = new Set(['folders']);

export class WorkspaceDBService extends Service {
  db: WorkspaceDBWithTables<AFFiNEWorkspaceDbSchema>;
  userdataDBPool = new ObjectPool<
    string,
    WorkspaceDB<AFFiNEWorkspaceUserdataDbSchema>
  >({
    onDangling() {
      return false; // never release
    },
  });

  /** Y.js docs for restricted tables that need deferred sync connection. */
  private readonly deferredSyncDocs = new Map<string, YDoc>();
  private readonly authorizedRestrictedTables = new Set<string>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
    const isCloud = this.workspaceService.workspace.flavour !== 'local';

    this.db = this.framework.createEntity(
      WorkspaceDB<AFFiNEWorkspaceDbSchema>,
      {
        db: new WorkspaceDBClient(
          new YjsDBAdapter(AFFiNE_WORKSPACE_DB_SCHEMA, {
            getDoc: guid => {
              const ydoc = new YDoc({
                // guid format: db${guid}
                guid: `db$${guid}`,
              });

              // sl-oiya.5: Restricted tables (folders) are NOT connected to
              // the sync engine here. For cloud workspaces, they are connected
              // later by connectRestrictedTable() only for Owner/Admin.
              // This prevents FORBIDDEN errors for Members trying to sync
              // restricted Y.js docs.
              if (isCloud && RESTRICTED_SYNC_TABLES.has(guid)) {
                if (this.authorizedRestrictedTables.has(guid)) {
                  this.connectDocWithPriority(ydoc);
                } else {
                  this.deferredSyncDocs.set(guid, ydoc);
                }
              } else {
                this.connectDocWithPriority(ydoc);
              }
              return ydoc;
            },
          })
        ),
        schema: AFFiNE_WORKSPACE_DB_SCHEMA,
        storageDocId: tableName => `db$${tableName}`,
      }
    ) as WorkspaceDBWithTables<AFFiNEWorkspaceDbSchema>;
  }

  /**
   * sl-oiya.5: Connect a restricted table's Y.js doc to the sync engine.
   * Called by FolderStore when the user is confirmed Owner/Admin.
   */
  connectRestrictedTable(tableName: string): void {
    if (!RESTRICTED_SYNC_TABLES.has(tableName)) return;
    this.authorizedRestrictedTables.add(tableName);
    const ydoc = this.deferredSyncDocs.get(tableName);
    if (!ydoc) return; // Already connected or not created yet
    this.deferredSyncDocs.delete(tableName);
    this.connectDocWithPriority(ydoc);
  }

  private connectDocWithPriority(ydoc: YDoc): void {
    this.workspaceService.workspace.engine.doc.connectDoc(ydoc);
    this.workspaceService.workspace.engine.doc.addPriority(ydoc.guid, 50);
  }

  userdataDB(userId: (string & {}) | '__local__') {
    // __local__ for local workspace
    const userdataDb = this.userdataDBPool.get(userId);
    if (userdataDb) {
      return userdataDb.obj as WorkspaceDBWithTables<AFFiNEWorkspaceUserdataDbSchema>;
    }

    const newDB = this.framework.createEntity(
      WorkspaceDB<AFFiNEWorkspaceUserdataDbSchema>,
      {
        db: new WorkspaceUserdataDBClient(
          new YjsDBAdapter(AFFiNE_WORKSPACE_USERDATA_DB_SCHEMA, {
            getDoc: guid => {
              const ydoc = new YDoc({
                // guid format: userdata${userId}${guid}
                guid: `userdata$${userId}$${guid}`,
              });
              this.workspaceService.workspace.engine.doc.connectDoc(ydoc);
              this.workspaceService.workspace.engine.doc.addPriority(
                ydoc.guid,
                50
              );
              return ydoc;
            },
          })
        ),
        schema: AFFiNE_WORKSPACE_USERDATA_DB_SCHEMA,
        storageDocId: tableName => `userdata$${userId}$${tableName}`,
      }
    );

    this.userdataDBPool.put(userId, newDB);
    return newDB as WorkspaceDBWithTables<AFFiNEWorkspaceUserdataDbSchema>;
  }

  authService = this.workspaceServerService.server?.scope.get(AuthService);
  public get userdataDB$() {
    // if is local workspace or no account, use __local__ userdata
    // sometimes we may have cloud workspace but no account for a short time, we also use __local__ userdata
    if (
      this.workspaceService.workspace.meta.flavour === 'local' ||
      !this.authService
    ) {
      return new LiveData(this.userdataDB('__local__'));
    } else {
      return this.authService.session.account$.map(account => {
        if (!account) {
          return this.userdataDB('__local__');
        }
        return this.userdataDB(account.id);
      });
    }
  }

  static isDBDocId(docId: string) {
    return docId.startsWith('db$') || docId.startsWith('userdata$');
  }
}
